import { z } from 'zod';

export const COMMON_ACCEPT_MIME =
  '.csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel,text/plain,.pdf,image/*';

export const RemoteFile = z.object({
  name: z.string(),
  url: z.string(),
});

export type RemoteFile = z.infer<typeof RemoteFile>;

export const LocalFileInner = z.object({
  file: z.instanceof(File),
  remote: z.string().url().nullish(),
});

export const LocalFile = LocalFileInner.box().describe(COMMON_ACCEPT_MIME);

export type LocalFile = z.infer<typeof LocalFile>;

export const OptionalLocalFile = LocalFileInner.optional().box().describe(COMMON_ACCEPT_MIME);

export type OptionalLocalFile = z.infer<typeof OptionalLocalFile>;

export type Upload = (p: { file: Blob[] }) => Promise<RemoteFile[]>;

export class FileTypeHelper {
  static remoteToLocal(v: RemoteFile): LocalFile {
    return {
      file: new File([], v.name),
      remote: v.url,
    };
  }

  static localToRemote(v: LocalFile): RemoteFile {
    return {
      name: v.file.name,
      url: v.remote ?? 'https://www.africau.edu/images/default/sample.pdf',
    };
  }

  /**
   *
   * @param files the mixture of local files and remote files in the form.
   * @param uploader
   * @returns an array of remote files that are all uploaded.
   * For already-remote files, they remain untouched.
   * For local files, they are uploaded using the `uploader` function.
   * The original order of files is kept.
   */
  static async uploadLocalArrayToRemote(
    files: LocalFile[],
    uploader: Upload,
  ): Promise<RemoteFile[]> {
    const indexMap = new Map<number, number>();

    const uploadFiles = files.flatMap((file, i) =>
      file.remote ? [] : indexMap.set(i, indexMap.size) && [file.file],
    );

    const uploadedFiles = uploadFiles.length > 0 ? await uploader({ file: uploadFiles }) : [];

    return files.map((file, i) =>
      file.remote ? FileTypeHelper.localToRemote(file) : uploadedFiles[indexMap.get(i)!],
    );
  }

  static async uploadLocal(v: LocalFile, uploader: Upload): Promise<RemoteFile> {
    if (v.remote) {
      return FileTypeHelper.localToRemote(v);
    }

    const [remoteFile] = await uploader({ file: [v.file] });

    return remoteFile;
  }
}
